import React from 'react';
import classNames from 'classnames/bind';
import { Navigate, useNavigate } from 'react-router-dom';

import { isNotNullOrUndefined } from 'services/nullable';
import { useAPI } from 'hooks/api/useAPI';
import { useCreateDeployment } from 'deployments/hooks/useCreateDeployment';
import { useCreateReferencePipelines } from 'cameras/hooks/useCreateReferencePipelines';
import { useCreateReferenceDeployments } from 'cameras/hooks/useCreateReferenceDeployments';
import { useStartCloudGateway } from 'gateways/hooks/useStartCloudGateway';
import { useMqttGatewaysState } from 'gateways/hooks/useMqttGatewaysState';
import { useStreams } from 'streams/hooks/useStreams';
import { useCurrentPlan } from 'organizations/hooks/useCurrentPlan';
import { usePipeline } from 'pipelines/hooks/usePipeline';

import { Camera } from 'types/camera';
import { Stream } from 'types/stream';
import { DeploymentFormContentRef } from 'deployments/components/Form/Content';
import { DeploymentFormProvider } from 'deployments/components/Form/context';
import { LightSwitch } from 'components/LightSwitch/LightSwitch';
import { OptionalTooltip } from 'components/Tooltip/OptionalTooltip';

import { useRapidDeployState } from '../hooks/useRapidDeployState';
import { RapidDeployRoute } from '../routes';
import { RapidDeployConfigurationForm } from './ConfigurationForm';
import { RapidDeployLayout } from './Layout';

import styles from '../RapidDeploy.module.scss';

const c = classNames.bind(styles);

export function RapidDeployDeployments() {
  const navigate = useNavigate();
  const { applicationID } = useAPI();
  const { isSimplifiedUxPlan, isRingPlan } = useCurrentPlan();
  const { gateway, videoSources, preselectedPipelineId } =
    useRapidDeployState();

  // Fetch the full pipeline object if we have a preselected pipeline ID
  const { data: preselectedPipeline } = usePipeline(preselectedPipelineId, {
    enabled: Boolean(preselectedPipelineId),
  });
  const { mutate: createReferencePipelines } = useCreateReferencePipelines();
  const { mutate: createReferenceDeployments } =
    useCreateReferenceDeployments();
  const { mutate: startCloudGateway, isLoading: isStartingCloudGateway } =
    useStartCloudGateway();

  // Subscribe to MQTT updates for gateway status
  useMqttGatewaysState(gateway ? [gateway.id] : []);

  // Auto-start OnDemand gateway if it's offline
  React.useEffect(() => {
    if (
      gateway &&
      gateway.model === 'Lumeo Cloud OnDemand' &&
      gateway.status !== 'online' &&
      gateway.status !== 'starting' &&
      !isStartingCloudGateway
    ) {
      startCloudGateway(gateway.id);
    }
  }, [gateway, startCloudGateway, isStartingCloudGateway]);

  // For Virtual cameras, select latest file stream as the video source
  const virtualCameraIds = React.useMemo(
    () =>
      videoSources
        .filter((s): s is Camera => !('stream_type' in s))
        .filter((c) => c.conn_type === 'virtual')
        .map((c) => c.id),
    [videoSources]
  );

  const { data: fileStreamsForVirtualCameras, isSuccess: hasFileStreams } =
    useStreams({
      params: {
        camera_ids: virtualCameraIds,
        stream_types: ['file'],
        limit: 100,
      },
      enabled: virtualCameraIds.length > 0,
      keepPreviousData: true,
    });

  const virtualCameraIdToLatestFileStream = React.useMemo(() => {
    const map = new Map<string, Stream>();
    if (!hasFileStreams || !fileStreamsForVirtualCameras) return map;
    for (const s of fileStreamsForVirtualCameras.data) {
      if (!s.camera_id) continue;
      const current = map.get(s.camera_id);
      if (!current) {
        map.set(s.camera_id, s);
        continue;
      }
      if (
        new Date(s.created_at).getTime() >
        new Date(current.created_at).getTime()
      ) {
        map.set(s.camera_id, s);
      }
    }
    return map;
  }, [fileStreamsForVirtualCameras, hasFileStreams]);

  const effectiveVideoSources = React.useMemo(
    () =>
      videoSources.map((src) => {
        if ('stream_type' in src) return src;
        if (src.conn_type !== 'virtual') return src;
        const replacement = virtualCameraIdToLatestFileStream.get(src.id);
        return replacement ?? src;
      }),
    [videoSources, virtualCameraIdToLatestFileStream]
  );

  const isGatewayOnline = gateway?.status === 'online';
  const canStartOnDeploy = !isRingPlan;
  const [shouldStartImmediately, setShouldStartImmediately] =
    React.useState(isGatewayOnline && canStartOnDeploy);
  const [, setError] = React.useState<string>();

  const [
    videoSourceIdsWithPipelineSelected,
    setVideoSourceIdsWithPipelineSelected,
  ] = React.useState(new Set<string>());

  // When a preselected pipeline is provided, automatically mark all video sources as having a pipeline
  React.useEffect(() => {
    if (preselectedPipeline && effectiveVideoSources.length > 0) {
      setVideoSourceIdsWithPipelineSelected(
        new Set(effectiveVideoSources.map((source) => source.id))
      );
    }
  }, [preselectedPipeline, effectiveVideoSources]);

  const formRefs = React.useRef<Array<DeploymentFormContentRef | null>>([]);
  const deploymentNameToVirtualCameraIdRef = React.useRef(
    new Map<string, string>()
  );

  const { mutate: createDeployment, isLoading } = useCreateDeployment({
    onSuccess(deployment, variables) {
      if (!gateway) {
        return;
      }

      // Determine if this deployment relates to a virtual camera
      // If so, add the pipeline and deployment ids to the virtual camera
      const cameraId = deploymentNameToVirtualCameraIdRef.current.get(
        String(variables.name)
      );
      if (cameraId) {
        createReferencePipelines({
          camera_id: cameraId,
          pipeline_ids: [variables.pipeline_id],
        });
        createReferenceDeployments({
          camera_id: cameraId,
          deployment_ids: [deployment.id],
        });
        deploymentNameToVirtualCameraIdRef.current.delete(
          String(variables.name)
        );
      }
    },
  });

  if (videoSources.length === 0) {
    return <Navigate to={`../${RapidDeployRoute.VIDEO_SOURCES}`} replace />;
  }

  async function handleContinueClick() {
    if (!formRefs.current || !gateway || !applicationID) {
      return;
    }

    const nonNullForms = formRefs.current.filter(isNotNullOrUndefined);
    const isValid = (
      await Promise.all(nonNullForms.map((form) => form.validate()))
    ).every(Boolean);

    // Only continue if all forms are valid
    if (isValid) {
      const formSubmissions = nonNullForms.map((form) => form.onSubmit());

      try {
        const configurations = await Promise.all(formSubmissions);

        // Create deployments for each form submission
        configurations.forEach(({ configuration, pipeline, videoSource }) => {
          // Determine the owning camera from the original selection
          let virtualCameraForDeployment: Camera | undefined;
          if (videoSource) {
            // If the video source is a file stream that belongs to a virtual camera,
            // we need to set references for the virtual camera
            if ('stream_type' in videoSource && videoSource.camera_id) {
              virtualCameraForDeployment = videoSources.find(
                (s): s is Camera => s.id === videoSource.camera_id
              );
            }
          }

          // Name deployments as Reference-* only for original Virtual cameras
          let deploymentName: string | null = null;

          if (virtualCameraForDeployment) {
            deploymentName = `Reference - ${virtualCameraForDeployment.name ?? ''} - ${pipeline.name}`;
            deploymentNameToVirtualCameraIdRef.current.set(
              deploymentName,
              virtualCameraForDeployment.id
            );
          }

          createDeployment({
            name: deploymentName,
            pipeline_id: pipeline.id,
            gateway_id: gateway.id,
            state: shouldStartImmediately ? 'running' : 'stopped',
            configuration,
          });
          setError(undefined);
        });

        // Finally, navigate based on simplified_ux mode
        if (isSimplifiedUxPlan && videoSources.length > 0) {
          // Get the first camera from videoSources
          const firstCamera = videoSources.find(
            (source): source is Camera => !('stream_type' in source)
          );

          if (firstCamera) {
            // Navigate to first camera's detail view -> settings tab
            navigate(`../../cameras/${firstCamera.id}`, {
              state: {
                tab: 'settings',
              },
            });
          } else {
            // Fallback to gateway deployments if no camera found
            navigate(`../../gateways/${gateway.id}`, {
              state: {
                tab: 'deployments',
              },
            });
          }
        } else {
          // Default behavior: navigate to the gateway-> deployments tab
          navigate(`../../gateways/${gateway.id}`, {
            state: {
              tab: 'deployments',
            },
          });
        }
      } catch (error) {
        setError(String(error));
      }
    }
  }

  return (
    <RapidDeployLayout
      backTo={RapidDeployRoute.VIDEO_SOURCES}
      continueText="Deploy"
      onContinue={handleContinueClick}
      isContinueLoading={isLoading}
      canContinue={effectiveVideoSources.every(({ id }) =>
        videoSourceIdsWithPipelineSelected.has(id)
      )}
      slot={
        canStartOnDeploy ? (
          <OptionalTooltip
            showTooltip={!isGatewayOnline}
            content="Gateway is offline. Cannot start immediately."
          >
            <div className={c('footer-actions')}>
              <LightSwitch
                onValueChange={setShouldStartImmediately}
                defaultChecked={isGatewayOnline}
                disabled={!isGatewayOnline}
              />
              <label className={c('footer-actions-label', 'text-label')}>
                Start immediately
              </label>
            </div>
          </OptionalTooltip>
        ) : undefined
      }
    >
      {effectiveVideoSources.map((videoSource, index) => {
        const virtualCameraName: string | undefined = (() => {
          if ('stream_type' in videoSource) {
            return videoSources.find(
              (s): s is Camera => s.id === videoSource.camera_id
            )?.name;
          }
          return undefined;
        })();

        return (
          <DeploymentFormProvider
            gateway={gateway}
            initialSource={videoSource}
            pipeline={preselectedPipeline}
            key={videoSource.id}
          >
            <RapidDeployConfigurationForm
              effectiveSourceName={virtualCameraName}
              videoSource={videoSource}
              isVirtualCameraSource={!!virtualCameraName}
              onPipelineSelect={() => {
                setVideoSourceIdsWithPipelineSelected((previous) =>
                  new Set(previous).add(videoSource.id)
                );
              }}
              defaultOpen={effectiveVideoSources.length === 1}
              ref={(el) => (formRefs.current[index] = el)}
            />
          </DeploymentFormProvider>
        );
      })}
    </RapidDeployLayout>
  );
}
